import React, { useState, useEffect } from 'react';
import { 
  Heart,
  Users,
  Lightbulb,
  TrendingUp,
  Target,
  CheckCircle,
  X,
  ArrowRight,
  Mail,
  Send,
  Award,
  AlertTriangle,
  Star,
  Zap,
  RefreshCw,
  Info,
  BarChart3,
  DollarSign,
  Clock,
  MessageSquare,
  Shield,
  Rocket,
  Eye,
  ChevronLeft,
  ChevronRight,
  Download,
  Globe,
  Building2
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// SEO Component
const SEOHead = () => {
  const title = "Innovation Health Check - Assess Your Organization's Innovation Capability | Lusidea";
  const description = "Free innovation assessment tool. Evaluate your organization's innovation culture, processes, and capabilities. Get personalized recommendations and benchmark against industry standards.";
  const keywords = "innovation assessment, innovation health check, innovation capability, innovation culture, business innovation, innovation maturity, innovation evaluation";
  
  useEffect(() => {
    // Set document title
    document.title = title;
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', description);
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }
    
    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    metaKeywords.setAttribute('content', keywords);
    if (!document.querySelector('meta[name="keywords"]')) {
      document.head.appendChild(metaKeywords);
    }
    
    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.setAttribute('content', title);
    if (!document.querySelector('meta[property="og:title"]')) {
      document.head.appendChild(ogTitle);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.setAttribute('content', description);
    if (!document.querySelector('meta[property="og:description"]')) {
      document.head.appendChild(ogDescription);
    }
    
    const ogType = document.querySelector('meta[property="og:type"]') || document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.setAttribute('content', 'website');
    if (!document.querySelector('meta[property="og:type"]')) {
      document.head.appendChild(ogType);
    }
    
    // Structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Innovation Health Check",
      "description": description,
      "url": "https://www.lusidea.com/tools/innovation-health-check",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web Browser",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "provider": {
        "@type": "Organization",
        "name": "Lusidea",
        "url": "https://www.lusidea.com"
      },
      "featureList": [
        "Innovation Culture Assessment",
        "Process Evaluation",
        "Leadership Support Analysis",
        "Resource Allocation Review",
        "Capability Benchmarking"
      ]
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    // Cleanup function
    return () => {
      const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('"WebApplication"')) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);
  
  return null;
};

// Enhanced 8-question assessment covering key innovation dimensions
const HEALTH_CHECK_QUESTIONS = [
  {
    id: 1,
    dimension: "Culture",
    question: "How does your organization approach new ideas?",
    subtitle: "This measures psychological safety and openness to innovation",
    icon: Users,
    options: [
      { text: "People freely share ideas and experiment without fear", score: 5, emoji: "🚀", desc: "Strong innovation culture" },
      { text: "Most people share ideas when encouraged", score: 4, emoji: "💪", desc: "Good cultural foundation" },
      { text: "Some people share ideas, but many hold back", score: 3, emoji: "🤔", desc: "Mixed innovation comfort" },
      { text: "Few people share ideas - playing it safe", score: 2, emoji: "😐", desc: "Risk-averse culture" },
      { text: "Ideas are rarely shared - innovation feels risky", score: 1, emoji: "😟", desc: "Innovation-resistant culture" }
    ]
  },
  {
    id: 2,
    dimension: "Leadership",
    question: "How does leadership support innovation efforts?",
    subtitle: "Leadership commitment is crucial for innovation success",
    icon: Award,
    options: [
      { text: "Leaders actively champion and participate in innovation", score: 5, emoji: "🎯", desc: "Innovation champion leaders" },
      { text: "Leaders support innovation and remove barriers", score: 4, emoji: "👍", desc: "Supportive leadership" },
      { text: "Leaders are interested but don't actively drive it", score: 3, emoji: "🤷", desc: "Passive leadership support" },
      { text: "Leaders say they support it but actions show otherwise", score: 2, emoji: "😕", desc: "Inconsistent leadership" },
      { text: "Leaders resist change and prefer status quo", score: 1, emoji: "❌", desc: "Change-resistant leadership" }
    ]
  },
  {
    id: 3,
    dimension: "Process",
    question: "How structured is your innovation process?",
    subtitle: "Clear processes help turn ideas into reality",
    icon: Target,
    options: [
      { text: "We have clear, well-defined innovation processes", score: 5, emoji: "📋", desc: "Mature innovation process" },
      { text: "We have some processes but they could be clearer", score: 4, emoji: "📝", desc: "Developing process structure" },
      { text: "Informal processes - depends on who's involved", score: 3, emoji: "🔄", desc: "Ad-hoc innovation approach" },
      { text: "Very little process - mostly random innovation", score: 2, emoji: "🎲", desc: "Unstructured innovation" },
      { text: "No clear process for moving ideas forward", score: 1, emoji: "❓", desc: "Chaotic innovation attempts" }
    ]
  },
  {
    id: 4,
    dimension: "Resources",
    question: "What resources are allocated to innovation?",
    subtitle: "Innovation requires dedicated time, budget, and people",
    icon: DollarSign,
    options: [
      { text: "Significant budget, time, and dedicated people for innovation", score: 5, emoji: "💰", desc: "Well-resourced innovation" },
      { text: "Decent resources with some dedicated innovation time", score: 4, emoji: "💵", desc: "Adequately resourced" },
      { text: "Limited resources - innovation happens when possible", score: 3, emoji: "🪙", desc: "Constrained resources" },
      { text: "Very few resources dedicated to new ideas", score: 2, emoji: "💸", desc: "Under-resourced innovation" },
      { text: "No dedicated resources for innovation activities", score: 1, emoji: "🚫", desc: "Resource-starved innovation" }
    ]
  },
  {
    id: 5,
    dimension: "Speed",
    question: "How quickly can you test and implement new ideas?",
    subtitle: "Speed of execution determines competitive advantage",
    icon: Zap,
    options: [
      { text: "We can test ideas within days and implement quickly", score: 5, emoji: "⚡", desc: "Rapid innovation cycles" },
      { text: "Testing takes weeks, implementation within months", score: 4, emoji: "🏃", desc: "Good innovation speed" },
      { text: "Testing takes months, implementation is slow", score: 3, emoji: "🚶", desc: "Moderate innovation pace" },
      { text: "Everything takes many months to test or implement", score: 2, emoji: "🐌", desc: "Slow innovation cycles" },
      { text: "Testing and implementation take years", score: 1, emoji: "🦕", desc: "Very slow innovation" }
    ]
  },
  {
    id: 6,
    dimension: "Customer",
    question: "How well do you involve customers in innovation?",
    subtitle: "Customer input ensures innovations solve real problems",
    icon: Heart,
    options: [
      { text: "Customers are deeply involved in our innovation process", score: 5, emoji: "🤝", desc: "Customer-centric innovation" },
      { text: "We regularly get customer feedback on new ideas", score: 4, emoji: "👂", desc: "Good customer engagement" },
      { text: "We sometimes ask customers about innovations", score: 3, emoji: "📞", desc: "Sporadic customer input" },
      { text: "We rarely involve customers in innovation", score: 2, emoji: "🔇", desc: "Limited customer involvement" },
      { text: "Innovation happens without customer input", score: 1, emoji: "🙈", desc: "Customer-disconnected innovation" }
    ]
  },
  {
    id: 7,
    dimension: "Learning",
    question: "How does your organization learn from innovation attempts?",
    subtitle: "Learning from both successes and failures drives improvement",
    icon: Lightbulb,
    options: [
      { text: "We systematically capture and share learnings", score: 5, emoji: "📚", desc: "Strong learning culture" },
      { text: "We usually discuss what worked and what didn't", score: 4, emoji: "💭", desc: "Good learning practices" },
      { text: "Some learning happens but inconsistently", score: 3, emoji: "🔍", desc: "Inconsistent learning" },
      { text: "Little effort to learn from innovation attempts", score: 2, emoji: "😶", desc: "Poor learning capture" },
      { text: "No systematic learning from innovation efforts", score: 1, emoji: "🕳️", desc: "No learning system" }
    ]
  },
  {
    id: 8,
    dimension: "Measurement",
    question: "How do you measure innovation success?",
    subtitle: "What gets measured gets managed and improved",
    icon: BarChart3,
    options: [
      { text: "Clear metrics track innovation impact and ROI", score: 5, emoji: "📊", desc: "Data-driven innovation" },
      { text: "We track some innovation metrics regularly", score: 4, emoji: "📈", desc: "Good measurement practices" },
      { text: "Informal tracking of innovation outcomes", score: 3, emoji: "📋", desc: "Basic measurement" },
      { text: "Very little measurement of innovation efforts", score: 2, emoji: "🤷", desc: "Poor measurement" },
      { text: "No systematic measurement of innovation", score: 1, emoji: "❓", desc: "No measurement system" }
    ]
  }
];

// Innovation maturity levels with detailed descriptions
const MATURITY_LEVELS = {
  beginner: {
    name: "Innovation Beginner",
    range: [1, 2.4],
    color: "red",
    emoji: "🌱",
    description: "Innovation is sporadic and unstructured",
    characteristics: [
      "Few dedicated resources for innovation",
      "Limited leadership support",
      "No clear innovation processes",
      "Risk-averse culture"
    ]
  },
  developing: {
    name: "Innovation Developing", 
    range: [2.5, 3.4],
    color: "orange",
    emoji: "🌿",
    description: "Some innovation happening but needs structure",
    characteristics: [
      "Occasional innovation projects",
      "Growing leadership interest",
      "Informal innovation processes",
      "Mixed comfort with risk"
    ]
  },
  competent: {
    name: "Innovation Competent",
    range: [3.5, 4.0],
    color: "blue", 
    emoji: "🌳",
    description: "Good innovation foundation with room to grow",
    characteristics: [
      "Regular innovation activities",
      "Supportive leadership",
      "Some structured processes",
      "Decent resource allocation"
    ]
  },
  advanced: {
    name: "Innovation Advanced",
    range: [4.1, 4.5],
    color: "green",
    emoji: "🏆",
    description: "Strong innovation capability across most areas",
    characteristics: [
      "Systematic innovation approach",
      "Strong leadership commitment",
      "Well-defined processes",
      "Good resource dedication"
    ]
  },
  leader: {
    name: "Innovation Leader",
    range: [4.6, 5.0],
    color: "purple",
    emoji: "🚀",
    description: "Industry-leading innovation capability",
    characteristics: [
      "Innovation embedded in culture",
      "Executive innovation champions",
      "Mature innovation systems",
      "Significant resource commitment"
    ]
  }
};

// Enhanced button component
const Button = ({ children, onClick, variant = 'primary', icon, disabled = false, className = '', size = 'md', fullWidth = false }) => {
  const variants = {
    primary: 'bg-red-600 hover:bg-red-700 active:bg-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500',
    outline: 'border-2 border-red-600 text-red-600 hover:bg-red-50 active:bg-red-100 focus:ring-red-500',
    secondary: 'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 text-gray-800 focus:ring-gray-500',
    success: 'bg-green-600 hover:bg-green-700 active:bg-green-800 text-white shadow-lg hover:shadow-xl focus:ring-green-500'
  };
  
  const sizes = {
    sm: 'py-2 px-4 text-sm min-h-[44px]',
    md: 'py-3 px-6 text-base min-h-[48px]',
    lg: 'py-4 px-8 text-lg min-h-[52px]'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${sizes[size]} rounded-xl font-semibold transition-all duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-offset-2 touch-manipulation ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${fullWidth ? 'w-full' : ''} ${className}`}
      aria-label={typeof children === 'string' ? children : 'Button'}
    >
      {icon && <span className="mr-2 flex-shrink-0" aria-hidden="true">{icon}</span>}
      <span className={fullWidth ? 'truncate' : ''}>{children}</span>
    </button>
  );
};

// Enhanced toast notification
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div 
      className={`fixed top-20 left-4 right-4 sm:top-24 sm:right-4 sm:left-auto z-50 px-4 py-4 rounded-xl shadow-lg border max-w-sm sm:max-w-md flex items-center gap-3 ${styles[type]}`}
      role="alert"
      aria-live="assertive"
    >
      <CheckCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <p className="font-medium flex-1 text-sm">{message}</p>
      <button 
        onClick={onClose} 
        className="hover:opacity-75 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Enhanced progress bar
const ProgressBar = ({ current, total, className = '' }) => {
  const percentage = (current / total) * 100;
  
  return (
    <div className={`w-full bg-gray-200 rounded-full h-2 sm:h-3 ${className}`}>
      <div 
        className="bg-red-600 h-2 sm:h-3 rounded-full transition-all duration-500 ease-out"
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Enhanced question component
const QuestionCard = ({ question, selectedAnswer, onAnswer, questionNumber, totalQuestions, onPrevious, onNext }) => {
  const QuestionIcon = question.icon;
  
  return (
    <article className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 max-w-4xl mx-auto">
      {/* Progress Header */}
      <header className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <QuestionIcon className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
            </div>
            <div>
              <div className="text-sm font-semibold text-red-600 uppercase tracking-wide">
                {question.dimension} Assessment
              </div>
              <div className="text-xs sm:text-sm text-gray-500">
                Question {questionNumber} of {totalQuestions}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl sm:text-3xl font-bold text-gray-900">
              {Math.round((questionNumber / totalQuestions) * 100)}%
            </div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
        </div>
        
        <ProgressBar current={questionNumber} total={totalQuestions} className="mb-4" />
        
        <div className="text-center">
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 leading-tight">
            {question.question}
          </h2>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
            {question.subtitle}
          </p>
        </div>
      </header>
      
      {/* Options */}
      <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
        {question.options.map((option, idx) => (
          <button
            key={idx}
            onClick={() => onAnswer(option.score)}
            disabled={selectedAnswer !== undefined}
            className={`w-full p-4 sm:p-5 text-left rounded-xl border-2 transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
              selectedAnswer === option.score
                ? 'border-red-500 bg-red-50 shadow-md transform scale-[1.02]'
                : selectedAnswer !== undefined
                ? 'border-gray-200 bg-gray-50 opacity-60'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
            aria-label={`Select option: ${option.text}`}
          >
            <div className="flex items-start gap-3 sm:gap-4">
              <span className="text-2xl sm:text-3xl flex-shrink-0">{option.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 text-sm sm:text-base mb-1 leading-tight">
                  {option.text}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">
                  {option.desc}
                </div>
              </div>
              {selectedAnswer === option.score && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 flex-shrink-0 animate-pulse" />
                  <span className="text-xs text-red-600 font-medium hidden sm:inline">Selected</span>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
      
      {/* Navigation */}
      <footer className="flex items-center justify-between">
        <Button
          onClick={onPrevious}
          variant="outline"
          icon={<ChevronLeft className="h-4 w-4" />}
          disabled={questionNumber === 1}
          className="flex-shrink-0"
          size="sm"
        >
          <span className="hidden sm:inline">Previous</span>
          <span className="sm:hidden">Back</span>
        </Button>
        
        <div className="text-center flex-1 mx-4">
          <div className="text-xs text-gray-500">
            {selectedAnswer ? "✓ Moving to next question..." : "Select an answer above"}
          </div>
        </div>
        
        {/* Only show Next button if no answer selected (as backup) */}
        {!selectedAnswer && (
          <Button
            onClick={onNext}
            variant="outline"
            icon={<ChevronRight className="h-4 w-4" />}
            disabled={true}
            className="flex-shrink-0 opacity-30"
            size="sm"
          >
            <span className="hidden sm:inline">Next</span>
            <span className="sm:hidden">Next</span>
          </Button>
        )}
        
        {selectedAnswer && (
          <div className="flex items-center gap-2 text-green-600">
            <CheckCircle className="h-4 w-4" />
            <span className="text-sm font-medium">Recorded</span>
          </div>
        )}
      </footer>
    </article>
  );
};

// Enhanced radar chart component
const RadarChart = ({ scores, size = 200 }) => {
  const dimensions = Object.keys(scores);
  const values = Object.values(scores);
  const center = size / 2;
  const radius = (size / 2) * 0.8;
  
  // Calculate points for pentagon
  const getPoint = (value, index) => {
    const angle = (index * 2 * Math.PI) / dimensions.length - Math.PI / 2;
    const distance = (value / 5) * radius;
    return {
      x: center + distance * Math.cos(angle),
      y: center + distance * Math.sin(angle)
    };
  };
  
  // Background pentagon rings
  const rings = [1, 2, 3, 4, 5];
  
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="drop-shadow-sm">
        {/* Background rings */}
        {rings.map(ring => {
          const points = dimensions.map((_, index) => getPoint(ring, index));
          const pathData = points.map((point, index) => 
            `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
          ).join(' ') + ' Z';
          
          return (
            <path
              key={ring}
              d={pathData}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="1"
              opacity={0.5}
            />
          );
        })}
        
        {/* Dimension lines */}
        {dimensions.map((_, index) => {
          const endPoint = getPoint(5, index);
          return (
            <line
              key={index}
              x1={center}
              y1={center}
              x2={endPoint.x}
              y2={endPoint.y}
              stroke="#e5e7eb"
              strokeWidth="1"
              opacity={0.5}
            />
          );
        })}
        
        {/* Data area */}
        {values.length > 0 && (
          <path
            d={values.map((value, index) => {
              const point = getPoint(value, index);
              return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`;
            }).join(' ') + ' Z'}
            fill="rgba(220, 38, 38, 0.2)"
            stroke="#dc2626"
            strokeWidth="2"
          />
        )}
        
        {/* Data points */}
        {values.map((value, index) => {
          const point = getPoint(value, index);
          return (
            <circle
              key={index}
              cx={point.x}
              cy={point.y}
              r="4"
              fill="#dc2626"
              stroke="white"
              strokeWidth="2"
            />
          );
        })}
      </svg>
      
      {/* Labels */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-xs">
        {dimensions.map((dimension, index) => (
          <div key={dimension} className="flex items-center gap-1">
            <div className="w-2 h-2 bg-red-600 rounded-full"></div>
            <span className="text-gray-700 font-medium">{dimension}</span>
            <span className="text-gray-500">({scores[dimension].toFixed(1)})</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Enhanced results screen with multiple visualizations
const ResultsScreen = ({ answers, onRestart, onGetReport }) => {
  // Calculate dimension scores
  const dimensionScores = {};
  HEALTH_CHECK_QUESTIONS.forEach(q => {
    dimensionScores[q.dimension] = answers[q.id] || 0;
  });
  
  const totalScore = Object.values(answers).reduce((sum, score) => sum + score, 0);
  const averageScore = totalScore / HEALTH_CHECK_QUESTIONS.length;
  const percentage = Math.round((averageScore / 5) * 100);
  
  // Determine maturity level
  const maturityLevel = Object.values(MATURITY_LEVELS).find(level => 
    averageScore >= level.range[0] && averageScore <= level.range[1]
  ) || MATURITY_LEVELS.beginner;
  
  // Industry benchmarks (example data)
  const industryBenchmarks = {
    "Top 10%": 4.2,
    "Industry Average": 3.1,
    "Your Score": averageScore
  };
  
  const getRecommendations = (score, dimension) => {
    const recommendations = {
      Culture: {
        low: "Create psychological safety for idea sharing",
        medium: "Establish innovation communities and forums", 
        high: "Maintain and celebrate your innovation culture"
      },
      Leadership: {
        low: "Get executive sponsors for innovation initiatives",
        medium: "Develop leadership innovation training programs",
        high: "Leaders should model innovation behavior"
      },
      Process: {
        low: "Implement a simple idea management system",
        medium: "Formalize stage-gate innovation processes",
        high: "Optimize and streamline existing processes"
      },
      Resources: {
        low: "Secure dedicated innovation budget and time",
        medium: "Increase resource allocation for innovation",
        high: "Optimize resource deployment for maximum impact"
      },
      Speed: {
        low: "Implement rapid prototyping capabilities",
        medium: "Reduce approval cycles and bureaucracy",
        high: "Maintain your competitive speed advantage"
      },
      Customer: {
        low: "Establish customer feedback loops",
        medium: "Increase customer co-creation activities",
        high: "Deepen customer partnership in innovation"
      },
      Learning: {
        low: "Create systems to capture lessons learned",
        medium: "Formalize knowledge sharing practices",
        high: "Continue systematic learning and sharing"
      },
      Measurement: {
        low: "Define basic innovation success metrics",
        medium: "Implement comprehensive innovation dashboards",
        high: "Optimize measurement for strategic insight"
      }
    };
    
    const level = score <= 2 ? 'low' : score <= 3.5 ? 'medium' : 'high';
    return recommendations[dimension][level];
  };
  
  return (
    <div className="max-w-6xl mx-auto space-y-6 sm:space-y-8">
      {/* Overall Score Card */}
      <section className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 text-center">
        <div className="text-6xl sm:text-8xl mb-4">{maturityLevel.emoji}</div>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
          Innovation Health Score
        </h2>
        
        {/* Circular progress meter */}
        <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-6">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              className="text-gray-200"
              strokeWidth="8"
              stroke="currentColor"
              fill="transparent"
              r="40"
              cx="50"
              cy="50"
            />
            <circle
              className={`text-${maturityLevel.color}-500 transition-all duration-1000`}
              strokeWidth="8"
              strokeDasharray={251}
              strokeDashoffset={251 - (percentage / 100) * 251}
              strokeLinecap="round"
              stroke="currentColor"
              fill="transparent"
              r="40"
              cx="50"
              cy="50"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl sm:text-3xl font-bold text-gray-900">{percentage}%</span>
            <span className="text-xs text-gray-600">({averageScore.toFixed(1)}/5.0)</span>
          </div>
        </div>
        
        <div className={`inline-block px-6 py-3 rounded-2xl bg-${maturityLevel.color}-100 text-${maturityLevel.color}-800 font-bold text-lg sm:text-xl mb-3`}>
          {maturityLevel.name}
        </div>
        <p className="text-gray-600 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
          {maturityLevel.description}
        </p>
      </section>
      
      {/* Detailed Analysis Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
        
        {/* Radar Chart */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <Eye className="h-6 w-6 text-red-600" />
            Capability Overview
          </h3>
          <RadarChart scores={dimensionScores} size={280} />
          <p className="text-sm text-gray-600 mt-4 text-center">
            Each axis represents a key innovation dimension rated 1-5
          </p>
        </section>
        
        {/* Benchmarking */}
        <section className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <BarChart3 className="h-6 w-6 text-red-600" />
            Industry Comparison
          </h3>
          <div className="space-y-4">
            {Object.entries(industryBenchmarks).map(([label, score]) => {
              const percentage = (score / 5) * 100;
              const isYours = label === "Your Score";
              
              return (
                <div key={label} className={`p-4 rounded-xl ${isYours ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={`font-semibold ${isYours ? 'text-red-900' : 'text-gray-700'}`}>
                      {label}
                    </span>
                    <span className={`font-bold ${isYours ? 'text-red-600' : 'text-gray-600'}`}>
                      {score.toFixed(1)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className={`h-3 rounded-full transition-all duration-1000 ${
                        isYours ? 'bg-red-600' : 'bg-gray-400'
                      }`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <strong>Benchmark Note:</strong> Scores based on assessments from 500+ organizations across various industries.
              </div>
            </div>
          </div>
        </section>
      </div>
      
      {/* Detailed Recommendations */}
      <section className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <Target className="h-6 w-6 text-red-600" />
          Personalized Action Plan
        </h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {Object.entries(dimensionScores).map(([dimension, score]) => {
            const question = HEALTH_CHECK_QUESTIONS.find(q => q.dimension === dimension);
            const QuestionIcon = question.icon;
            const recommendation = getRecommendations(score, dimension);
            
            const scoreColor = score <= 2 ? 'red' : score <= 3.5 ? 'yellow' : 'green';
            const scoreColorClasses = {
              red: 'bg-red-50 border-red-200 text-red-800',
              yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800', 
              green: 'bg-green-50 border-green-200 text-green-800'
            };
            
            return (
              <div key={dimension} className={`p-4 rounded-xl border-2 ${scoreColorClasses[scoreColor]}`}>
                <div className="flex items-center gap-2 mb-3">
                  <QuestionIcon className="h-5 w-5" />
                  <span className="font-bold text-sm">{dimension}</span>
                  <span className="ml-auto font-bold">{score.toFixed(1)}</span>
                </div>
                <p className="text-xs leading-relaxed">{recommendation}</p>
              </div>
            );
          })}
        </div>
      </section>
      
      {/* Maturity Characteristics */}
      <section className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8">
        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
          <Award className="h-6 w-6 text-red-600" />
          Your Current Innovation Profile
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {maturityLevel.characteristics.map((characteristic, idx) => (
            <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <span className="text-gray-800 font-medium text-sm">{characteristic}</span>
            </div>
          ))}
        </div>
      </section>
      
      {/* Actions */}
      <section className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button 
          onClick={onGetReport} 
          icon={<Mail className="h-5 w-5" />}
          size="lg"
          className="px-8"
        >
          Get Detailed Action Plan
        </Button>
        <Button 
          onClick={onRestart} 
          variant="outline" 
          icon={<RefreshCw className="h-5 w-5" />}
          size="lg"
        >
          Retake Assessment
        </Button>
      </section>
    </div>
  );
};

// Enhanced report capture with better UX
const ReportCapture = ({ onSubmit, isSubmitting, scores }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    industry: ''
  });
  
  const industries = [
    'Technology', 'Healthcare', 'Financial Services', 'Manufacturing', 
    'Retail', 'Education', 'Government', 'Non-profit', 'Other'
  ];
  
  const handleSubmit = () => {
    if (!formData.email || !formData.email.includes('@')) return;
    onSubmit({ ...formData, scores });
  };
  
  const isFormValid = formData.name && formData.email && formData.company;
  
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 max-w-lg mx-auto">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Award className="h-8 w-8 text-red-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-3">Get Your Complete Action Plan</h3>
        <p className="text-gray-600 leading-relaxed">
          Receive a personalized 15-page report with detailed recommendations, 
          benchmarking data, and a 90-day implementation roadmap.
        </p>
      </div>
      
      <div className="space-y-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
              placeholder="Your name"
              className="w-full p-3 border border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
              placeholder="your@email.com"
              className="w-full p-3 border border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all min-h-[44px]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
              Company *
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => setFormData(prev => ({...prev, company: e.target.value}))}
              placeholder="Company name"
              className="w-full p-3 border border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-semibold text-gray-700 mb-2">
              Role
            </label>
            <input
              type="text"
              id="role"
              value={formData.role}
              onChange={(e) => setFormData(prev => ({...prev, role: e.target.value}))}
              placeholder="Your role"
              className="w-full p-3 border border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all min-h-[44px]"
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="industry" className="block text-sm font-semibold text-gray-700 mb-2">
            Industry
          </label>
          <select
            id="industry"
            value={formData.industry}
            onChange={(e) => setFormData(prev => ({...prev, industry: e.target.value}))}
            className="w-full p-3 border border-gray-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none transition-all min-h-[44px]"
          >
            <option value="">Select industry</option>
            {industries.map(industry => (
              <option key={industry} value={industry}>{industry}</option>
            ))}
          </select>
        </div>
      </div>
      
      <Button
        onClick={handleSubmit}
        disabled={!isFormValid || isSubmitting}
        icon={<Send className="h-4 w-4" />}
        size="lg"
        fullWidth
        className="mb-4"
      >
        {isSubmitting ? 'Generating Report...' : 'Send My Action Plan'}
      </Button>
      
      <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Download className="h-4 w-4" />
          Your report includes:
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Detailed dimension analysis and benchmarking</li>
          <li>• Industry-specific recommendations</li>
          <li>• 90-day implementation roadmap</li>
          <li>• ROI calculations and success metrics</li>
        </ul>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        Report typically delivered within 2 hours during business days
      </p>
    </div>
  );
};

// Enhanced success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center max-w-lg mx-auto">
      <div className="w-20 h-20 mx-auto bg-green-100 rounded-2xl flex items-center justify-center mb-6">
        <CheckCircle className="h-10 w-10 text-green-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">Report Delivered!</h3>
      <p className="text-gray-600 mb-6 leading-relaxed">
        Your comprehensive innovation action plan has been sent to <strong>{email}</strong>.
      </p>
      
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 mb-6">
        <h4 className="font-semibold text-blue-900 mb-3">What happens next?</h4>
        <div className="text-sm text-blue-800 text-left space-y-2">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-blue-800">1</span>
            </div>
            <span>Review your personalized action plan</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-blue-800">2</span>
            </div>
            <span>Our team will follow up within 24 hours</span>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-xs font-bold text-blue-800">3</span>
            </div>
            <span>Optional strategy session to discuss implementation</span>
          </div>
        </div>
      </div>
      
      <p className="text-sm text-gray-600">
        Questions? Email us at <a href="mailto:hello@lusidea.com" className="text-red-600 hover:underline">hello@lusidea.com</a>
      </p>
    </div>
  );
};

// Main Component
const InnovationHealthCheck = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [showReportCapture, setShowReportCapture] = useState(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [toast, setToast] = useState(null);
  const [isStarted, setIsStarted] = useState(false);
  
  const handleAnswer = (score) => {
    const newAnswers = { ...answers, [HEALTH_CHECK_QUESTIONS[currentQuestion].id]: score };
    setAnswers(newAnswers);
    
    // Auto-advance after selection with a brief delay for visual feedback
    setTimeout(() => {
      if (currentQuestion < HEALTH_CHECK_QUESTIONS.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        // Assessment complete - small delay before showing results
        setTimeout(() => setShowResults(true), 300);
      }
    }, 800);
  };
  
  const handleNext = () => {
    if (currentQuestion < HEALTH_CHECK_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Assessment complete
      setShowResults(true);
    }
  };
  
  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  const handleStart = () => {
    setIsStarted(true);
    setCurrentQuestion(0);
  };
  
  const handleReportSubmit = (data) => {
    setIsSubmittingReport(true);
    setTimeout(() => {
      setIsSubmittingReport(false);
      setReportSubmitted(true);
      setSubmittedEmail(data.email);
      setShowReportCapture(false);
      setToast({ message: 'Action plan sent to your email!', type: 'success' });
    }, 2000);
  };
  
  const restart = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setShowReportCapture(false);
    setReportSubmitted(false);
    setIsStarted(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <SEOHead />
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header */}
      <header className="bg-white pt-20 sm:pt-24 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
            <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            Innovation Health Check
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Assess your organization's innovation capability across 8 key dimensions. 
            Get personalized recommendations and benchmark against industry standards.
          </p>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Start Screen */}
        {!isStarted && (
          <div className="max-w-4xl mx-auto">
            <section className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 sm:p-8 text-center mb-8">
              <div className="text-6xl sm:text-8xl mb-6">🏥</div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Ready for your innovation health assessment?
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed text-base sm:text-lg max-w-2xl mx-auto">
                Our comprehensive 8-question assessment evaluates your organization's innovation 
                maturity across critical dimensions. Get instant results with detailed insights.
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
                <div className="text-center p-4 sm:p-6">
                  <div className="text-3xl sm:text-4xl mb-3">⚡</div>
                  <h3 className="font-bold text-gray-900 mb-2">5 Minutes</h3>
                  <p className="text-sm text-gray-600">Comprehensive yet quick</p>
                </div>
                <div className="text-center p-4 sm:p-6">
                  <div className="text-3xl sm:text-4xl mb-3">📊</div>
                  <h3 className="font-bold text-gray-900 mb-2">Detailed Analysis</h3>
                  <p className="text-sm text-gray-600">Visual insights & benchmarking</p>
                </div>
                <div className="text-center p-4 sm:p-6">
                  <div className="text-3xl sm:text-4xl mb-3">🎯</div>
                  <h3 className="font-bold text-gray-900 mb-2">Action Plan</h3>
                  <p className="text-sm text-gray-600">Personalized recommendations</p>
                </div>
              </div>
              
              <Button 
                onClick={handleStart}
                icon={<ArrowRight className="h-5 w-5" />}
                size="lg"
                className="text-lg px-8 py-4 shadow-lg"
              >
                Start Assessment
              </Button>
            </section>
            
            <section className="bg-blue-50 rounded-2xl p-6 sm:p-8 border border-blue-200">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                <div className="flex-shrink-0">
                  <Info className="h-8 w-8 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-blue-900 mb-4 text-lg sm:text-xl">Innovation Dimensions We Assess:</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {HEALTH_CHECK_QUESTIONS.map((question) => {
                      const QuestionIcon = question.icon;
                      return (
                        <div key={question.dimension} className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <QuestionIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <span className="font-medium text-blue-800">{question.dimension}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
        
        {/* Questions */}
        {isStarted && currentQuestion < HEALTH_CHECK_QUESTIONS.length && !showResults && (
          <QuestionCard
            question={HEALTH_CHECK_QUESTIONS[currentQuestion]}
            selectedAnswer={answers[HEALTH_CHECK_QUESTIONS[currentQuestion].id]}
            onAnswer={handleAnswer}
            questionNumber={currentQuestion + 1}
            totalQuestions={HEALTH_CHECK_QUESTIONS.length}
            onPrevious={handlePrevious}
            onNext={handleNext}
          />
        )}
        
        {/* Results */}
        {showResults && !showReportCapture && !reportSubmitted && (
          <ResultsScreen
            answers={answers}
            onRestart={restart}
            onGetReport={() => setShowReportCapture(true)}
          />
        )}
        
        {/* Report Capture */}
        {showReportCapture && (
          <ReportCapture
            onSubmit={handleReportSubmit}
            isSubmitting={isSubmittingReport}
            scores={answers}
          />
        )}
        
        {/* Success */}
        {reportSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      {/* Enhanced mobile styles */}
      <style jsx global>{`
        * { 
          scroll-behavior: smooth; 
          -webkit-tap-highlight-color: transparent;
        }
        
        .touch-manipulation {
          touch-action: manipulation;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          button, input, textarea, select { 
            min-height: 44px;
            font-size: 16px; /* Prevents zoom on iOS */
          }
          
          input:focus, textarea:focus, select:focus {
            transform: none; /* Prevents jump on focus */
          }
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar { 
          width: 4px;
        }
        ::-webkit-scrollbar-track { 
          background: #f1f5f9; 
          border-radius: 2px; 
        }
        ::-webkit-scrollbar-thumb { 
          background: #cbd5e1; 
          border-radius: 2px; 
        }
        ::-webkit-scrollbar-thumb:hover { 
          background: #94a3b8; 
        }
        
        /* Improved focus states for accessibility */
        button:focus-visible, 
        input:focus-visible, 
        textarea:focus-visible,
        select:focus-visible {
          outline: 2px solid #dc2626;
          outline-offset: 2px;
        }
        
        /* Better tap targets */
        @media (pointer: coarse) {
          button {
            min-height: 48px;
            min-width: 48px;
          }
        }
      `}</style>
    </div>
  );
};

export default InnovationHealthCheck;